import { authFetch } from "@/lib/api-client";
import type { EventJobStatus } from "@/features/events/api/events.api";

export type ScheduledJobState = "ENABLED" | "PAUSED" | "DISABLED" | "UPDATE_FAILED" | "UNKNOWN";
export type ScheduledJobRunOutcome = "OK" | "ERROR" | "UNKNOWN";

export interface ScheduledJobLastAttempt {
  time: string | null;
  outcome: ScheduledJobRunOutcome;
  errorMessage: string | null;
}

export interface ScheduledJobRecentRun {
  id: string;
  correlationId: string;
  status: EventJobStatus;
  publishedAt: string;
  runDurationMs: number | null;
  errorMessage: string | null;
  /** True when Cloud Scheduler fired this run, as opposed to a manual "run now" publish. */
  triggeredBySchedule: boolean;
}

/** Consecutive-failure streak and last success, computed over `recentRuns` (most recent first). */
export interface ScheduledJobHealth {
  lastSuccessAt: string | null;
  consecutiveFailures: number;
}

/** Cloud Scheduler RetryConfig subset (Advanced / power depth). Durations in seconds. */
export interface ScheduledJobRetryConfig {
  retryCount?: number;
  maxRetryDurationSeconds?: number;
  minBackoffDurationSeconds?: number;
  maxBackoffDurationSeconds?: number;
  maxDoublings?: number;
}

export interface ScheduledJobPubsubTarget {
  topicName: string;
  attributes: Record<string, string>;
  bodyJson: unknown | null;
}

export interface ScheduledJob {
  name: string;
  id: string;
  description: string | null;
  schedule: string | null;
  timeZone: string | null;
  state: ScheduledJobState;
  nextScheduleTime: string | null;
  lastAttempt: ScheduledJobLastAttempt | null;
  /** Set only when the job's Pub/Sub payload decodes to a known Nexus event. */
  eventType: string | null;
  /** Whether `eventType` is on the triggerable allowlist, so "run now" can be offered. */
  isTriggerable: boolean;
  pubsubTarget: ScheduledJobPubsubTarget | null;
  retryConfig: ScheduledJobRetryConfig | null;
  /** Job attempt deadline in seconds; null when unset. Ignored by GCP for Pub/Sub delivery. */
  attemptDeadlineSeconds: number | null;
  health: ScheduledJobHealth | null;
  recentRuns: ScheduledJobRecentRun[];
}

/** Power + parity Advanced payload — optional on create/update. */
export interface ScheduledJobAdvancedInput {
  topicName?: string;
  attributes?: Record<string, string>;
  bodyJson?: unknown;
  retryConfig?: ScheduledJobRetryConfig | null;
  attemptDeadlineSeconds?: number | null;
}

/** Curated create — server fills topic / attributes / body from the triggerable catalog. */
export interface CreateScheduledJobInput {
  id: string;
  eventName: string;
  description?: string | null;
  schedule: string;
  timeZone: string;
  enabled: boolean;
  advanced?: ScheduledJobAdvancedInput;
}

/** MVP + power PATCH fields. Job id/name are immutable after create. */
export interface UpdateScheduledJobInput {
  description?: string | null;
  schedule?: string;
  timeZone?: string;
  advanced?: ScheduledJobAdvancedInput;
}

export async function fetchScheduledJobs(windowHours: number): Promise<ScheduledJob[]> {
  const { data } = await authFetch<ScheduledJob[]>(
    `/admin/scheduled-jobs?windowHours=${windowHours}`,
  );
  return data;
}

export async function createScheduledJob(input: CreateScheduledJobInput): Promise<ScheduledJob> {
  const { data } = await authFetch<ScheduledJob>("/admin/scheduled-jobs", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data;
}

export async function updateScheduledJob(
  jobId: string,
  input: UpdateScheduledJobInput,
): Promise<ScheduledJob> {
  const { data } = await authFetch<ScheduledJob>(
    `/admin/scheduled-jobs/${encodeURIComponent(jobId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );
  return data;
}

export async function pauseScheduledJob(jobId: string): Promise<ScheduledJob> {
  const { data } = await authFetch<ScheduledJob>(
    `/admin/scheduled-jobs/${encodeURIComponent(jobId)}/pause`,
    { method: "POST" },
  );
  return data;
}

export async function resumeScheduledJob(jobId: string): Promise<ScheduledJob> {
  const { data } = await authFetch<ScheduledJob>(
    `/admin/scheduled-jobs/${encodeURIComponent(jobId)}/resume`,
    { method: "POST" },
  );
  return data;
}

/**
 * Destructive delete — `confirmJobId` must match `jobId` or the API rejects
 * the request (same string the dialog requires the operator to type).
 */
export async function deleteScheduledJob(jobId: string, confirmJobId: string): Promise<void> {
  await authFetch(`/admin/scheduled-jobs/${encodeURIComponent(jobId)}`, {
    method: "DELETE",
    body: JSON.stringify({ confirmJobId }),
  });
}

/** Response from `POST /admin/scheduled-jobs/:jobId/run-now`. */
export interface RunNowScheduledJobResult {
  correlationId: string;
  eventName: string;
  topic: string;
}

export async function runNowScheduledJob(jobId: string): Promise<RunNowScheduledJobResult> {
  const { data } = await authFetch<RunNowScheduledJobResult>(
    `/admin/scheduled-jobs/${encodeURIComponent(jobId)}/run-now`,
    { method: "POST" },
  );
  return data;
}
